'use client';

/**
 * Single source of truth for the Pendo identity GuestPass sends, for both
 * audiences (coordinators and guests).
 *
 * Account (identical shape for both audiences, sent only when BOTH the tenant
 * and the organisation code are known — otherwise `account` is omitted):
 *   id:                  `${tenantId}_${organisationCode}`
 *   tenantId
 *   masterCorporateCode: organisationCode
 * `organisationCode` is the Zeno master corporate code, so GuestPass activity
 * lands on the same Pendo account as Zeno and picks up Zeno's account metadata.
 * Nothing else is ever sent on the account (no `host`, `organisationId`,
 * `role` or `solUserId`): Pendo metadata is last-write-wins, so any field Zeno
 * also sends with a different value would flip back and forth.
 *
 * Coordinator visitor (same id Zeno uses):
 *   id: `${tenantId}_${solUserId}`, tenantId, solUserId, role: 'coordinator',
 *   guestPassHost
 *
 * Guest visitor (kept distinct from corporate users):
 *   id: `${tenantId}_guest_${guestPassGuestId}` — guestPassGuestId is the EMS
 *   Guest.Id, a stable id across the funnel. `solUserId` carries the linked SOL
 *   user id (Guest.RefUserId) when known: a positive refUserId is sent as a
 *   string, an explicit 0 sends '' (clears any stale value), and an undefined
 *   refUserId omits the key so Pendo keeps the metadata it already has.
 */

type PendoApi = {
  initialize: (config: unknown) => void;
  identify?: (config: unknown) => void;
  isReady?: () => boolean;
  showGuideById?: (guideId: string) => void;
  guides?: unknown[];
};

type PendoWindow = Window & { pendo?: PendoApi };

const getPendo = (): PendoApi | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as PendoWindow).pendo;
};

export type PendoAccount = {
  id: string;
  tenantId: string;
  masterCorporateCode: string;
};

export type PendoVisitor = {
  id: string;
  tenantId: string;
  role: 'coordinator' | 'guest';
  solUserId?: string;
  guestPassGuestId?: string;
  guestPassHost?: string;
};

export type PendoConfig = {
  visitor: PendoVisitor;
  account?: PendoAccount;
};

export const buildPendoAccount = (params: {
  tenantId?: string;
  organisationCode?: string;
}): PendoAccount | undefined =>
  params.tenantId && params.organisationCode
    ? {
        id: `${params.tenantId}_${params.organisationCode}`,
        tenantId: params.tenantId,
        masterCorporateCode: params.organisationCode,
      }
    : undefined;

const withAccount = (
  visitor: PendoVisitor,
  account: PendoAccount | undefined
): PendoConfig => (account ? { visitor, account } : { visitor });

/** A guest id is usable when it is non-empty and not 0. */
const isUsableGuestId = (id: string | number | undefined): boolean =>
  id !== undefined && id !== null && String(id) !== '' && Number(id) !== 0;

/**
 * Returns undefined when `refUserId` is not provided (key omitted), the string
 * id when it is > 0, and '' when it is explicitly 0 (or otherwise not positive).
 */
const resolveSolUserId = (
  refUserId: string | number | undefined
): string | undefined => {
  if (refUserId === undefined || refUserId === null || refUserId === '') {
    return undefined;
  }
  const parsed = Number(refUserId);
  if (Number.isNaN(parsed)) return undefined;
  return parsed > 0 ? String(refUserId) : '';
};

export const buildGuestPendoConfig = (params: {
  tenantId?: string;
  guestPassGuestId?: string | number;
  refUserId?: string | number;
  organisationCode?: string;
  host?: string;
}): PendoConfig | undefined => {
  if (!params.tenantId || !isUsableGuestId(params.guestPassGuestId))
    return undefined;

  const guestPassGuestId = String(params.guestPassGuestId);
  const solUserId = resolveSolUserId(params.refUserId);

  const visitor: PendoVisitor = {
    id: `${params.tenantId}_guest_${guestPassGuestId}`,
    tenantId: params.tenantId,
    guestPassGuestId,
    ...(solUserId !== undefined ? { solUserId } : {}),
    role: 'guest',
    ...(params.host ? { guestPassHost: params.host } : {}),
  };

  return withAccount(
    visitor,
    buildPendoAccount({
      tenantId: params.tenantId,
      organisationCode: params.organisationCode,
    })
  );
};

export const buildCoordinatorPendoConfig = (params: {
  tenantId?: string;
  solUserId?: string;
  organisationCode?: string;
  host?: string;
}): PendoConfig | undefined => {
  if (!params.tenantId || !params.solUserId) return undefined;

  const visitor: PendoVisitor = {
    id: `${params.tenantId}_${params.solUserId}`,
    tenantId: params.tenantId,
    solUserId: params.solUserId,
    role: 'coordinator',
    ...(params.host ? { guestPassHost: params.host } : {}),
  };

  return withAccount(
    visitor,
    buildPendoAccount({
      tenantId: params.tenantId,
      organisationCode: params.organisationCode,
    })
  );
};

/**
 * initialize() on the first call in the page, identify() afterwards (initialize
 * only takes effect once per page load, so a later identity change — e.g. guest
 * verification adding the account — needs identify()). Returns false when the
 * Pendo agent is not present.
 */
export const applyPendoIdentity = (config: PendoConfig): boolean => {
  const pendo = getPendo();
  if (!pendo) return false;

  if (typeof pendo.isReady === 'function' && pendo.isReady()) {
    pendo.identify?.(config);
  } else {
    pendo.initialize(config);
  }
  return true;
};

/**
 * Pushes a newly known SOL user id (Guest.RefUserId, set when the guest
 * completes their profile) onto the existing guest visitor. Metadata-only
 * update via identify(); returns false when there is nothing to send.
 */
export const updateGuestSolUserId = (params: {
  tenantId?: string;
  guestPassGuestId?: string | number;
  refUserId?: string | number;
}): boolean => {
  const pendo = getPendo();
  if (
    !pendo ||
    typeof pendo.identify !== 'function' ||
    !params.tenantId ||
    !isUsableGuestId(params.guestPassGuestId) ||
    !(Number(params.refUserId) > 0)
  ) {
    return false;
  }
  pendo.identify({
    visitor: {
      id: `${params.tenantId}_guest_${String(params.guestPassGuestId)}`,
      solUserId: String(params.refUserId),
    },
  });
  return true;
};
